import {useEffect, useState} from 'react';
import {toast} from 'react-toastify';

import {attributionService} from '@/services/attributionService';
import type {AttributionEventType} from '@/types/attribution';
import {getErrorMessage} from '@/utils/errorMessage';

interface AttributionParams {
  startDate: string;
  endDate: string;
  eventTypes: AttributionEventType[];
}

type AttributionResponse = {
  [K in AttributionEventType]: number;
};

interface AttributionResult {
  data: AttributionResponse | null;
  isLoading: boolean;
  error: Error | null;
}

export const useAttributionData = (
  params: AttributionParams,
): AttributionResult => {
  const [data, setData] = useState<AttributionResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const result = await attributionService.getAttributionData(params);
        if (isMounted) {
          setData(result);
        }
      } catch (err) {
        if (isMounted) {
          toast.error(getErrorMessage(err, 'An error occurred'));
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    // Fire-and-forget: fetchData catches and toasts its own failures, so this
    // only stops an unexpected throw becoming an unhandled rejection.
    fetchData().catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [params.startDate, params.endDate, params.eventTypes.join(',')]);

  return {data, isLoading, error};
};
